// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useCallback } from 'react';

import type { UnitOfMeasure, UnitOfMeasureId } from '@evolvemep/api-ts';
import { useUncontrolled } from '@mantine/hooks';

import type { WrappedSelectProps } from 'components/Mantine/TypeSafeSelect/types';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { NewUnitOfMeasureModal } from 'hooks/typeCacheHooks/NewUnitOfMeasureModal';
import { SPOOL_UOM_ID } from 'modules/Shop/WorkOrders/WorkOrderDetail/constants';

import { useUnitOfMeasure } from './useUnitOfMeasure';

type Props = {
  value?: UnitOfMeasure | null;
  onChange: (value: UnitOfMeasure | null) => void;
} & Omit<WrappedSelectProps<UnitOfMeasureId, object>, 'data' | 'value' | 'onChange' | 'creatable'>;

export const UnitOfMeasureSelect = ({ value: parentValue, onChange: parentOnChange, ...props }: Props) => {
  const { uoms, loading } = useUnitOfMeasure();

  const [value, onChange] = useUncontrolled<UnitOfMeasure | null>({
    value: parentValue,
    onChange: parentOnChange,
  });

  const data = useMemo(
    () =>
      uoms
        ?.filter((u) => u.unitOfMeasureId !== SPOOL_UOM_ID)
        .map((u) => ({
          label: `${u.name} (${u.code})`,
          value: u.unitOfMeasureId,
        })) ?? [],
    [uoms],
  );

  const wrappedOnChange = useCallback(
    (unitOfMeasureId: UnitOfMeasureId | null) => {
      const unitOfMeasure = uoms?.find((u) => u.unitOfMeasureId === unitOfMeasureId);
      onChange(unitOfMeasure ?? value);
    },
    [uoms, onChange, value],
  );

  return (
    <WrappedSelect
      label="UOM"
      searchable
      maxLength={32}
      creatable
      createLabel="Create new UOM"
      creationModal={(defaultValue, modalProps) => (
        <NewUnitOfMeasureModal onAdded={onChange} defaultValue={defaultValue} {...modalProps} />
      )}
      loading={loading}
      {...props}
      value={value?.unitOfMeasureId ?? null}
      onChange={wrappedOnChange}
      data={data}
    />
  );
};
